// src/utils/stockDocs.js
// P4-6 (31 Oct 2026, D-319) - which inventory_summary doc holds an item's stock,
// in ONE place.
//
// WHY THIS FILE EXISTS
// Stock List, Stock Adjustment, Inventory Dashboard, Material Summary and the
// Procurement Queue each read inventory_summary their own way, and the deleted
// legacy triggers left junk docs there (current_stock / total_in, no qty; MED-22).
// So Stock List showed junk docs as items, and Stock Adjustment kept the higher
// of qty / current_stock, which let a junk doc replace the real one. Now:
//   - a real stock doc has a numeric qty; junk docs are never shown, only counted
//   - the doc = doc id item_id, else the ONE real doc with that item_id (the same
//     order as resolveSummaryRef()); 2+ real docs are never summed
//   - stock = qty only, a real 0 stays 0; current_stock / total_in are never read
//   - the item list comes from Item Master (pickerItems), so an item with no
//     stock doc is listed at 0 and can get its opening stock
//   - what cannot be placed is listed under attention with the reason
// Pure (no Firebase) -> covered by src/utils/stockDocs.test.js (Jest).

import { matchItem } from "./itemMatch";
import { pickerItems } from "./itemPicker";
import { roundQty } from "./stockPlan";

export const TOOL_CATEGORIES = ["TOOLS", "ASSET", "ASSETS"];

const clean = (v) => String(v ?? "").trim();
const upper = (v) => clean(v).toUpperCase();

export const hasToolCode = (code) => /^(AS|TL|AST)-/.test(upper(code));

// Tools and assets are not store stock: Stock Adjustment leaves them out.
export const isToolRow = (row) =>
  TOOL_CATEGORIES.includes(upper(row?.category)) || hasToolCode(row?.item_code);

export const isRealStockDoc = (data) =>
  typeof data?.qty === "number" && Number.isFinite(data.qty);

export const whyNotMatched = (r) => (r.status === "ambiguous"
  ? `${r.candidates.length} items match — not counted`
  : "Not found in Item Master");

export const tooManyDocsReason = (n) => `${n} stock records for this item — not counted`;

/**
 * Same order as resolveSummaryRef(): doc id = item_id, else the ONE doc.
 * @param {Array}  docsOfItem real stock docs of one item ({ id, ... })
 * @returns {Object|null} that doc; null = 2+ docs and none has the item's id
 */
export const pickStockDoc = (docsOfItem = [], itemId) =>
  docsOfItem.find((d) => d.id === clean(itemId)) ||
  (docsOfItem.length === 1 ? docsOfItem[0] : null);

/**
 * @param {Array} master     Item Master docs ({ id, ... }), inactive included
 * @param {Array} stockDocs  inventory_summary docs as { id, data }
 * @returns {{ byItem: Map, unplaced: Array, junkDocs: number }}
 *   byItem: item_id -> { item, docs: [{ id, data }] }; unplaced: real docs that
 *   match no single item, with the reason and their qty
 */
export function groupStockDocs(master = [], stockDocs = []) {
  const byItem = new Map();
  const unplaced = [];
  let junkDocs = 0;

  for (const { id, data } of stockDocs) {
    if (!isRealStockDoc(data)) { junkDocs += 1; continue; }

    const itemId = clean(data.item_id) || clean(id);
    const r = matchItem(master, { item_id: itemId, item_code: data.item_code, item_name: data.item_name });
    if (!r.item) {
      unplaced.push({
        reason: whyNotMatched(r), item_id: itemId, item_code: data.item_code,
        item_name: data.item_name || itemId, unit: data.unit, qty: data.qty,
      });
      continue;
    }

    const canonicalId = clean(r.item.item_id) || itemId;
    if (!byItem.has(canonicalId)) byItem.set(canonicalId, { item: r.item, docs: [] });
    byItem.get(canonicalId).docs.push({ id, data });
  }

  return { byItem, unplaced, junkDocs };
}

/**
 * One row per active item (own + shared ""), sorted by name then code.
 * @param {Object} input
 * @param {Array}  input.items      Item Master docs ({ id, ... }), inactive included
 * @param {Array}  input.stockDocs  inventory_summary docs as { id, data }
 * @param {string} input.companyId
 * @returns {{ rows: Array, attention: Array, junkDocs: number,
 *             inactiveWithStock: { items: number, qty: number } }}
 */
export function buildStockRows({ items = [], stockDocs = [], companyId } = {}) {
  const master = items.filter((it) => it && (it.company_id === companyId || it.company_id === ""));
  const { byItem, unplaced, junkDocs } = groupStockDocs(master, stockDocs);

  const rows = [];
  const attention = [...unplaced];
  const listed = new Set(unplaced.map((u) => clean(u.item_id)));

  for (const item of pickerItems(master, companyId)) {
    const id = clean(item.item_id);
    const shown = { item_id: id, item_code: item.item_code, item_name: item.item_name, unit: item.unit, qty: 0 };

    if (item._blocked) {
      if (!id || !listed.has(id)) attention.push({ ...shown, reason: item._blocked });
      listed.add(id);
      continue;
    }

    const docs = byItem.get(id)?.docs || [];
    const picked = pickStockDoc(docs, id);
    if (docs.length > 0 && !picked) {
      attention.push({ ...shown, reason: tooManyDocsReason(docs.length) });
      continue;
    }
    if (docs.length > 1) {
      attention.push({ ...shown, qty: picked.data.qty, reason: `${docs.length} stock records for this item — only ${picked.id} counted` });
    }

    const stock = picked?.data;
    rows.push({
      item_id:         id,
      item_code:       clean(item.item_code) || clean(stock?.item_code),
      item_name:       item.item_name,
      category:        item.item_category || stock?.category || "",
      unit:            item.unit || stock?.unit || "",
      qty:             stock ? stock.qty : 0,
      defective_stock: Number(stock?.defective_stock || 0),
      scrap_stock:     Number(stock?.scrap_stock || 0),
      minimum_stock:   Number(stock?.minimum_stock || item.minimum_stock || 0),
      updated_at:      stock?.updated_at || stock?.created_at || null,
      stock_doc_id:    picked?.id || "",
      no_stock_doc:    !picked,
    });
  }

  const inactiveWithStock = { items: 0, qty: 0 };
  for (const [id, { item, docs }] of byItem) {
    if (item.is_active !== false) continue;
    const qty = pickStockDoc(docs, id)?.data.qty || 0;
    if (qty === 0) continue;
    inactiveWithStock.items += 1;
    inactiveWithStock.qty = roundQty(inactiveWithStock.qty + qty);
  }

  attention.sort((a, b) => clean(a.item_name).localeCompare(clean(b.item_name)));
  return { rows, attention, junkDocs, inactiveWithStock };
}

// Same rule as Home (Dashboard.js): an item with no minimum never alerts.
export const isLowStock = (row) => row.minimum_stock > 0 && row.qty <= row.minimum_stock;

/**
 * The Inventory Dashboard cards. No total qty: it added metres and nos together,
 * which means nothing (P4-8).
 */
export const stockTotals = (rows = []) => ({
  totalItems:         rows.length,
  lowStock:           rows.filter(isLowStock).length,
  defectiveStock:     rows.reduce((sum, r) => roundQty(sum + r.defective_stock), 0),
  negativeStockItems: rows.filter((r) => r.qty < 0).length,
});

/** Defective Stock page (P4-8): items with a real stock doc, last updated first. */
export const defectiveRows = (rows = []) =>
  rows
    .filter((r) => !r.no_stock_doc)
    .sort((a, b) => (b.updated_at?.seconds || 0) - (a.updated_at?.seconds || 0));
